import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { trpc } from "~/utils/trpc";
import type { inferRouterOutputs } from "@trpc/server";
import type { AppRouter } from "~be/router/index";

export type RouterOutputs = inferRouterOutputs<AppRouter>;
export type Team = RouterOutputs["team"]["list"][number];
export type Project = RouterOutputs["project"]["list"][number];

interface TeamProjectContextValue {
  selectedTeamId: string;
  setSelectedTeamId: (id: string, persist?: boolean) => void;
  selectedProjectId: string;
  setSelectedProjectId: (id: string, persist?: boolean) => void;
  teams: Team[];
  projects: Project[];
  isLoadingTeams: boolean;
  isLoadingProjects: boolean;
  isSettling: boolean;
}

const TeamProjectContext = createContext<TeamProjectContextValue | undefined>(undefined);

export function TeamProjectProvider({ children }: { children: React.ReactNode }) {
  const [selectedTeamId, setSelectedTeamId] = useState<string>("");
  const [selectedProjectId, setSelectedProjectId] = useState<string>("");

  const handleSetSelectedTeamId = useCallback((teamId: string, persist = true) => {
    setSelectedTeamId(teamId);
    if (!persist) return;
    try {
      localStorage.setItem("selectedTeamId", teamId);
    } catch {}
  }, []);

  const handleSetSelectedProjectId = useCallback((projectId: string, persist = true) => {
    setSelectedProjectId(projectId);
    if (!persist) return;
    try {
      localStorage.setItem("selectedProjectId", projectId);
    } catch {}
  }, []);

  const { data: teams, isLoading: isLoadingTeams } = trpc.team.list.useQuery();
  const { data: projects, isLoading: isLoadingProjects } = trpc.project.list.useQuery(
    { teamId: selectedTeamId },
    { enabled: Boolean(selectedTeamId) },
  );

  const activeProjects = useMemo(() => projects ?? [], [projects]);

  const selectedNotInActiveList =
    Boolean(selectedTeamId && selectedProjectId) &&
    !isLoadingProjects &&
    !activeProjects.some((p) => p.id === selectedProjectId);

  const {
    data: resolvedOrphanProject,
    isFetching: isResolvingOrphanProject,
    isError: isOrphanProjectResolveError,
  } = trpc.project.resolve.useQuery(
    { projectId: selectedProjectId! },
    { enabled: selectedNotInActiveList, staleTime: 30_000 },
  );

  const selectFirstActiveOrClear = useCallback(() => {
    if (activeProjects.length > 0) {
      handleSetSelectedProjectId(activeProjects[0].id);
    } else {
      handleSetSelectedProjectId("", true);
    }
  }, [activeProjects, handleSetSelectedProjectId]);

  /**
   * Pick team in one effect and read localStorage here (not a separate mount effect).
   * Otherwise, when `teams` loads, the auto-select effect can run in the same tick as
   * `setState` from localStorage still sees `selectedTeamId === ""` and overwrites with
   * `teams[0]` (newest team), even though localStorage had a connected team selected.
   */
  useEffect(() => {
    if (isLoadingTeams || !teams?.length) return;

    let persistedTeam = "";
    try {
      persistedTeam = localStorage.getItem("selectedTeamId") ?? "";
    } catch {
      // ignore
    }

    const persistedTeamValid =
      Boolean(persistedTeam) && teams.some((t) => t.id === persistedTeam);

    if (persistedTeamValid) {
      if (selectedTeamId !== persistedTeam) {
        handleSetSelectedTeamId(persistedTeam, false);
      }
      return;
    }

    if (selectedTeamId && teams.some((t) => t.id === selectedTeamId)) return;

    handleSetSelectedTeamId(teams[0].id);
  }, [teams, isLoadingTeams, selectedTeamId, handleSetSelectedTeamId]);

  useEffect(() => {
    if (!selectedTeamId || isLoadingProjects) return;

    let persistedProject = "";
    try {
      persistedProject = localStorage.getItem("selectedProjectId") ?? "";
    } catch {
      // ignore
    }

    const inActiveList = (id: string) =>
      Boolean(id) && activeProjects.some((p) => p.id === id);

    if (persistedProject && inActiveList(persistedProject)) {
      if (selectedProjectId !== persistedProject) {
        handleSetSelectedProjectId(persistedProject, false);
      }
      return;
    }

    if (selectedProjectId && inActiveList(selectedProjectId)) {
      return;
    }

    if (selectedProjectId && !inActiveList(selectedProjectId)) {
      if (isResolvingOrphanProject) {
        return;
      }

      if (isOrphanProjectResolveError || !resolvedOrphanProject) {
        selectFirstActiveOrClear();
        return;
      }

      if (resolvedOrphanProject.teamId !== selectedTeamId) {
        selectFirstActiveOrClear();
        return;
      }

      const isArchived = Boolean(resolvedOrphanProject.archivedAt);
      if (isArchived) {
        selectFirstActiveOrClear();
        return;
      }

      return;
    }

    if (persistedProject && !inActiveList(persistedProject)) {
      if (selectedProjectId !== persistedProject) {
        handleSetSelectedProjectId(persistedProject, false);
      }
      return;
    }

    if (activeProjects.length > 0) {
      handleSetSelectedProjectId(activeProjects[0].id);
    } else if (selectedProjectId) {
      handleSetSelectedProjectId("", true);
    }
  }, [
    selectedTeamId,
    isLoadingProjects,
    activeProjects,
    selectedProjectId,
    handleSetSelectedProjectId,
    isResolvingOrphanProject,
    isOrphanProjectResolveError,
    resolvedOrphanProject,
    selectFirstActiveOrClear,
  ]);

  const teamList = teams ?? [];
  const projectList = activeProjects;
  const isSettling =
    isLoadingTeams ||
    // Teams loaded but no team selected yet (selection effect pending)
    (teamList.length > 0 && !selectedTeamId) ||
    // Team selected but projects still loading
    (Boolean(selectedTeamId) && isLoadingProjects) ||
    // Resolving a project ID that is not in the active list (e.g. archived)
    (selectedNotInActiveList && isResolvingOrphanProject) ||
    // Active projects exist but none selected yet (selection effect pending)
    (Boolean(selectedTeamId) &&
      !isLoadingProjects &&
      projectList.length > 0 &&
      !selectedProjectId &&
      !selectedNotInActiveList);

  return (
    <TeamProjectContext.Provider
      value={{
        selectedTeamId,
        setSelectedTeamId: handleSetSelectedTeamId,
        selectedProjectId,
        setSelectedProjectId: handleSetSelectedProjectId,
        teams: teamList,
        projects: projectList,
        isLoadingTeams,
        isLoadingProjects,
        isSettling,
      }}
    >
      {children}
    </TeamProjectContext.Provider>
  );
}

export function useTeamProject() {
  const ctx = useContext(TeamProjectContext);
  if (!ctx) throw new Error("useTeamProject must be used within TeamProjectProvider");
  return ctx;
}
